import React, { useContext, useEffect, useMemo, useState } from 'react';
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import { useLocation } from 'react-router-dom';

import dayjs from 'dayjs';
import * as isoWeek from 'dayjs/plugin/isoWeek';

// Types.
import { ChangeTime } from "../../types/instance/Change";
import { EventTime } from "../../types/instance/Event";
import { AlertTime } from "../../types/instance/Alert";
import EventsDataTable from "../../types/instance/tables/EventsDataTable";

// Constants.

// Helpers.
import { getConvertedTimeToUTC, highchartsCredits } from "../../helpers/utils";
import Helper from "../../helpers/Helper";
import EventsApi from '../../api/instance/EventsApi';
import { chartOptionsGranularity } from "../../helpers/chartOptionsIncreasedGranularity";
import { showError } from "../../utils/showAlert";

// Components.
import HostTarget from "../../types/host/HostTarget";
import EventType from "../../types/instance/EventType";
import InstanceTarget from "../../types/instance/InstanceTarget";
import {
    CHART_DATA_TYPE,
    COLOUR_ALERTS,
    COLOUR_CHANGES,
    COLOUR_EVENTS,
    DATA_FAILED,
    DATA_INITIALISING, DATA_LOADED,
    DATA_LOADING
} from "../../component/Constants";
import WidgetCard from "../../component/instance/widget";
import EventsTable from "../../component/instance/table/EventsTable";
import FilterPanel from '../../component/FilterPanel';

// Context
import { TimeRangeContext } from "../../context/TimeRangeContext";
import { LicenceContext } from "../../context/LicenceContext";

// Hooks
import { useInstanceFilters } from "../../hooks/useInstanceFilters";

// DayJS plugins - note, the position of these in this file are important.
dayjs.extend(isoWeek.default);


function EventHistoryContainer(props: {
    eventTypes: EventType[],
    instances?: InstanceTarget[],
    instance?: InstanceTarget
    host?: HostTarget,
    hosts?: HostTarget[],
    applyPeriod: Function
}) {

    const [loading, setLoading] = useState<number>(DATA_INITIALISING);
    const [rawChanges, setRawChanges] = useState<ChangeTime[]>([]);
    const [rawCustomEvents, setRawCustomEvents] = useState<EventTime[]>([]);
    const [rawAlerts, setRawAlerts] = useState<AlertTime[]>([]);
    const [eventsDataTable, setEventsDataTable] = useState<EventsDataTable[]>([])

    const timeRangeContext = useContext(TimeRangeContext)
    const licences = useContext(LicenceContext)
    const location = useLocation()

    // Use instances from props when available (already loaded with tags by Content) to avoid duplicate /tags API calls
    // Memoize to avoid new array reference every render (e.g. [props.instance]) which would cause infinite loop in useInstanceFilters
    const instancesToUse = useMemo(
        () => (props.instance ? [props.instance] : props.instances ?? []),
        [props.instance, props.instances]
    );
    const shouldFetch = !props.instance && !props.host && (!instancesToUse || instancesToUse.length === 0);

    // Use custom hook for filtering
    const {
        filters,
        updateFilter,
        clearAllFilters,
        instances,
        availableFilterOptions,
        availableHostTypes,
        getFilteredInstances,
        filtersLoading
    } = useInstanceFilters({
        instances: instancesToUse,
        shouldFetch
    });
    const isHost = location.pathname.startsWith('/hosts/')

    useEffect(() => {
        async function load() {
            try {
                setLoading(DATA_LOADING);
                let query1 = '?' + timeRangeContext.getTimeRangeQueryString();
                let query2 = query1;

                if (props.instance) {
                    query1 += '&datasourceid=' + props.instance.id;
                    query2 += '&id=' + props.instance.id;
                } else if (props.host) {
                    query1 += '&hostid=' + props.host.id;
                    query2 += '&hostid=' + props.host.id;
                }

                const results = await Promise.allSettled
                ([
                    EventsApi.fetchEventV2(query1).then(async r => {
                                if (!r.ok) {
                                    const errorText = await r.text();
                                    throw new Error(errorText || r.statusText);
                                }
                                return r.json();
                            }),
                    EventsApi.fetchChangeTime(query2).then(async r => {
                                if (!r.ok) {
                                    const errorText = await r.text();
                                    throw new Error(errorText || r.statusText);
                                }
                                return r.json();
                            }),
                    EventsApi.fetchEventTime(query2).then(async r => {
                                if (!r.ok) {
                                    const errorText = await r.text();
                                    throw new Error(errorText || r.statusText);
                                }
                                return r.json();
                            }),
                    EventsApi.fetchAlertTime(query2).then(async r => {
                                if (!r.ok) {
                                    const errorText = await r.text();
                                    throw new Error(errorText || r.statusText);
                                }
                                return r.json();
                            })
                ]);

                // Check for failures
                const failedCalls: Array<{name: string, error: string}> = [];
                const endpoints = ['Events', 'Changes', 'Custom Events', 'Alerts'];
                results.forEach((result, index) => {
                    if (result.status === 'rejected') {
                        failedCalls.push({
                            name: endpoints[index],
                            error: result.reason?.message || 'Unknown error'
                        });
                    }
                });

                if (failedCalls.length > 0) {
                    const errorDetails = failedCalls.map(f => `${f.name}: ${f.error}`).join('; ');
                    throw new Error(errorDetails);
                }if (active) {
                    // Event type does not match actual data...
                    const topN/*: Event[]*/ = results[0].status === 'fulfilled' ? results[0].value : [];
                    const changes: ChangeTime[] = results[1].status === 'fulfilled' ? results[1].value : [];
                    const customEvents: EventTime[] = results[2].status === 'fulfilled' ? results[2].value : [];
                    const alerts: AlertTime[] = results[3].status === 'fulfilled' ? results[3].value : [];

                    const tableData: EventsDataTable[] =
                            topN.map((event: any, index: number) => {
                                let htmlIcon = '';
                                if (event.event === 'custom') {
                                    // Look up the custom event type definition to get its icon
                                    const eventTypeDef = props.eventTypes?.find(et => et.title === event.type);
                                    htmlIcon = eventTypeDef?.htmlIconCode || 'fas fa-cog';
                                }
                                const tableEvent: EventsDataTable =
                                        {
                                            id: index,
                                            instanceId: event.datasourceid,
                                            changeType: event.type,
                                            changeFrom: event.oldvalue,
                                            changeTo: event.newvalue,
                                            title: event.name,
                                            htmlIconCode: htmlIcon,
                                            instanceName: event.entity,
                                            detailsUrl: event.detailsUrl,
                                            color: event.colourCode,
                                            details: event.description,
                                            timeslice: event.timeslice,
                                            timesliceEnd: undefined,
                                            event: event.event,
                                            isHost: !!props.host || event.hostid,
                                            host: props.host || ((props.hosts?.length && props.hosts.find(h => h.id === event.hostid)) || undefined),
                                            startedEnded: event.startedended,
                                            statistic: event.statistic,
                                            units: event.units,
                                            threshold: event.threshold,
                                            decreasing: event.decreasing
                                        };
                                return tableEvent;
                            });

                    setRawChanges(changes);
                    setRawCustomEvents(customEvents);
                    setRawAlerts(alerts);
                    setEventsDataTable(tableData);
                    setLoading(DATA_LOADED);
                }
            } catch (x: any) {
                console.error(x.message);
                showError('Failed to load event history data', x.message || 'An error occurred while loading the data');
                setRawChanges([]);
                setRawCustomEvents([]);
                setRawAlerts([]);
                setEventsDataTable([]);
                setLoading(DATA_FAILED);
            }
        }

        let active: boolean = true;
        load();
        return function () { active = false; }
    }, [timeRangeContext, /*props.applyPeriod,*/ props.instance, props.host, licences.licences]);

    // Create a Set of instance IDs that pass all filters
    const filteredInstanceIds = useMemo(() => {
        // If viewing a specific instance or host, or no instances loaded, don't filter
        if (props.instance || props.host || instances.length === 0) {
            return null;
        }

        // Use the hook's filtering helper
        const filtered = getFilteredInstances();
        return new Set(filtered.map(inst => inst.id));
    }, [instances, props.instance, props.host, getFilteredInstances]);

    // Filter events based on instance filters
    const filteredEventsDataTable = useMemo(() => {
        // If no filtering needed (viewing specific instance/host or no filters applied)
        if (filteredInstanceIds === null) {
            return eventsDataTable;
        }

        // Filter events to only those belonging to filtered instances
        return eventsDataTable.filter(event =>
                filteredInstanceIds.has(event.instanceId)
        );
    }, [eventsDataTable, filteredInstanceIds]);

    // Compute filtered chart data and chart component
    const { chart, peakChanges, peakCustomEvents, peakAlerts } = useMemo(() => {
        let changes = rawChanges;
        let customEvents = rawCustomEvents;
        let alerts = rawAlerts;

        // If we're viewing all instances and have filters, build chart data from events
        if (!props.instance && !props.host && instances.length > 0 && filteredInstanceIds && eventsDataTable.length > 0) {
            // Build time-series data from filtered events
            const buildTimeSeriesFromEvents = (eventType: string) => {
                const timeMap = new Map<string, number>();

                eventsDataTable.forEach((event: EventsDataTable) => {
                    if (filteredInstanceIds.has(event.instanceId) && event.event === eventType) {
                        // Convert Date to string in ISO format
                        const timeStr = typeof event.timeslice === 'string'
                                ? event.timeslice
                                : new Date(event.timeslice).toISOString();
                        timeMap.set(timeStr, (timeMap.get(timeStr) || 0) + 1);
                    }
                });

                return Array.from(timeMap.entries())
                        .map(([timeslice, count]) => ({ timeslice, count }))
                        .sort((a, b) => a.timeslice.localeCompare(b.timeslice));
            };

            changes = buildTimeSeriesFromEvents('change');
            customEvents = buildTimeSeriesFromEvents('custom');
            alerts = buildTimeSeriesFromEvents('alert');
        }

        const peakChanges = changes.reduce((max, e) => Math.max(max, e.count || 0), 0);
        const peakCustomEvents = customEvents.reduce((max, e) => Math.max(max, e.count || 0), 0);
        const peakAlerts = alerts.reduce((max, e) => Math.max(max, e.count || 0), 0);

        // Align all series to common timestamps for proper stacking (Changes, Custom Events, Alerts)
        const interval = timeRangeContext.timeRange
            ? Helper.getInterval(
                timeRangeContext.timeRange.from,
                timeRangeContext.timeRange.to,
                timeRangeContext.timeRange.interval
            )
            : 60000;
        const bucket = (dat: { timeslice: string | number }) => {
            const ts = getConvertedTimeToUTC(dat);
            return Math.floor(ts / interval) * interval;
        };
        const toMap = (arr: { timeslice: string | number; count?: number }[]) => {
            const m = new Map<number, number>();
            arr.forEach((dat) => {
                const key = bucket(dat);
                m.set(key, (m.get(key) || 0) + (dat.count ?? 0));
            });
            return m;
        };
        const changesMap = toMap(changes);
        const customEventsMap = toMap(customEvents);
        const alertsMap = toMap(alerts);
        const allTimestamps = new Set([
            ...Array.from(changesMap.keys()),
            ...Array.from(customEventsMap.keys()),
            ...Array.from(alertsMap.keys())
        ]);
        const sortedTimestamps = Array.from(allTimestamps).sort((a, b) => a - b);

        const chartSeries = [
            ...(isHost ? [] : [
                {
                    color: COLOUR_CHANGES,
                    type: 'column',
                    data: sortedTimestamps.map((ts) => [ts, changesMap.get(ts) ?? 0]),
                    name: 'Changes'
                },
                {
                    color: COLOUR_EVENTS,
                    type: 'column',
                    data: sortedTimestamps.map((ts) => [ts, customEventsMap.get(ts) ?? 0]),
                    name: 'Custom Events'
                }
            ]),
            {
                color: COLOUR_ALERTS,
                type: 'column',
                data: sortedTimestamps.map((ts) => [ts, alertsMap.get(ts) ?? 0]),
                name: 'Alerts'
            }
        ];

        const chartOptions = chartOptionsGranularity(
                props.applyPeriod,
                chartSeries,
                Math.max(peakChanges, peakCustomEvents, peakAlerts),
                {
                    credits: {
                        enabled: highchartsCredits(licences.licences)
                    },
                    legend: true,
                    timeRangeContext: timeRangeContext,
                    tooltip: {
                        formatter: function () {
                            return Helper.getChartTooltipsNew(this, CHART_DATA_TYPE.GENERIC);
                        }
                    },
                    columnGapSize: 0.25,
                    columnPointPlacement: null
                },
                200
        );

        const chart = <HighchartsReact
                useUtcconstructorType={"chart"}
                highcharts={Highcharts}
                options={chartOptions}/>;

        return { chart, peakChanges, peakCustomEvents, peakAlerts };
    }, [rawChanges, rawCustomEvents, rawAlerts, eventsDataTable, filteredInstanceIds, props.instance, props.host, instances, isHost, props.applyPeriod, timeRangeContext, licences.licences]);

    // Calculate filtered totals based on filtered events
    const { filteredTotalEvents, filteredTotalChanges, filteredTotalCustomEvents, filteredTotalAlerts } = useMemo(() => {
        const totalEvents = filteredEventsDataTable.length;

        // Calculate totals by event type
        let calcTotalChanges = 0;
        let calcTotalCustomEvents = 0;
        let calcTotalAlerts = 0;

        filteredEventsDataTable.forEach(event => {
            if (event.event === 'change') {
                calcTotalChanges += 1;
            } else if (event.event === 'custom') {
                calcTotalCustomEvents += 1;
            } else if (event.event === 'alert') {
                calcTotalAlerts += 1;
            }
        });

        return {
            filteredTotalEvents: totalEvents,
            filteredTotalChanges: calcTotalChanges,
            filteredTotalCustomEvents: calcTotalCustomEvents,
            filteredTotalAlerts: calcTotalAlerts
        };
    }, [filteredEventsDataTable]);

    return (
            <React.Fragment>
                {/* Metric Widgets - Instances */}
                <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3">
                    {!isHost && (
                            <WidgetCard
                                    name="Changes"
                                    iconClassName="changes"
                                    icon="fa-exchange"
                                    value={filteredTotalChanges}
                                    cardTooltipText="The total auto-detected changes count"
                            />
                    )}
                    {!isHost && (
                            <WidgetCard
                                    name="Custom Events"
                                    iconClassName="calendar"
                                    icon="fa-calendar"
                                    value={filteredTotalCustomEvents}
                                    cardTooltipText="The total custom events count"
                            />
                    )}
                    <WidgetCard
                            name="Alerts"
                            iconClassName="alerts"
                            icon="fa-exclamation-triangle"
                            value={filteredTotalAlerts}
                            cardTooltipText="The total alerts count"
                    />
                </div>

                {/* Filter Panel - Only show when not viewing specific instance or host */}
                {!props.instance && !props.host && instances.length > 0 && (
                        <div className="row">
                            <div className="col">
                                <FilterPanel
                                        filterGroups={[
                                            {
                                                name: 'sensorstatus',
                                                label: 'Sensor Status',
                                                options: availableFilterOptions.sensorstatuses,
                                                selectedValues: filters.sensorstatus || [],
                                                onSelectionChange: (values: string[]) => updateFilter('sensorstatus', values)
                                            },
                                            {
                                                name: 'type',
                                                label: 'Type',
                                                options: availableFilterOptions.types,
                                                selectedValues: filters.type,
                                                onSelectionChange: (values: string[]) => updateFilter('type', values)
                                            },
                                            {
                                                name: 'tag',
                                                label: 'Tag',
                                                options: availableFilterOptions.tags,
                                                selectedValues: filters.tag,
                                                onSelectionChange: (values: string[]) => updateFilter('tag', values)
                                            },
                                            {
                                                name: 'host',
                                                label: 'Host',
                                                options: availableFilterOptions.hosts,
                                                selectedValues: filters.host,
                                                onSelectionChange: (values: string[]) => updateFilter('host', values)
                                            },
                                            {
                                                name: 'hostType',
                                                label: 'Host Type',
                                                options: availableHostTypes,
                                                selectedValues: filters.hostType,
                                                onSelectionChange: (values: string[]) => updateFilter('hostType', values)
                                            }
                                        ]}
                                        onClearAll={clearAllFilters}
                                        isLoading={filtersLoading}
                                />
                            </div>
                        </div>
                )}

                {/* Charts */}
                <div className="row">
                    <div className="col">
                        <div className="card collapsible">
                            <div className="card-header">
                                <i className="fal fa-chart-bar fa-fw" aria-hidden="true"/>
                                Changes and events over time
                                <span className="badge bg-info"
                                      data-tooltip-id="global-tooltip" data-tooltip-content="Total event count">{filteredTotalEvents.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                                <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                                   data-bs-target="#collapseCharts" aria-expanded="false" aria-controls="collapseCharts"/>
                            </div>
                            <div id="collapseCharts" className="card-body collapse show p-0">
                                <div className="row">
                                    <div className="col p-3">
                                        <h4>Changes and events<span
                                                className="peak">Peak Changes: {peakChanges}, Custom Events: {peakCustomEvents}, Alerts: {peakAlerts}</span>
                                        </h4>
                                        {chart}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Change History */}
                <div className="row">
                    <div className="col">
                        <div className="card collapsible">
                            <div className="card-header">
                                <i className="fal fa-exchange fa-fw" aria-hidden="true"/>
                                {isHost ? 'Alerts' : 'Change and event history'}
                                <span className="badge bg-info"
                                      data-tooltip-id="global-tooltip" data-tooltip-content="Event count">{filteredTotalEvents.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                                <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                                   data-bs-target="#collapseTable" aria-expanded="false" aria-controls="collapseTable"/>
                            </div>
                            <div id="collapseTable" className="card-body collapse show">
                                <EventsTable loading={loading} eventsDataTable={filteredEventsDataTable}/>
                            </div>
                        </div>
                    </div>
                </div>
            </React.Fragment>
    )
}

export default EventHistoryContainer;
